import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Chip,
  ClickAwayListener,
  Divider,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';

// third-party
import {
  useMsal,
} from '@azure/msal-react';

// project imports
import MainCard from '../../../../components/common/card/Card';
import Transitions from '../../../../components/extended/Transitions';
import WarningIcon from '../../../../assets/images/icons/warning_amber.svg';
import useAuth from '../../../../hooks/useAuth';
// assets
import { IconLogout } from '@tabler/icons-react';
import './profile.scss';
import { USER_TYPE, applicationTitle } from '../../../../constants';
import useAuthExternalUser from '../../../../hooks/useAuthExternalUser';
import AlertBox from '../../../../components/common/alert/AlertBox';
// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
  const theme = useTheme();
  const { accounts } = useMsal();
  const { externalUserLogout, externalUserDetails, getUserType } = useAuthExternalUser();
  const { userPermissions } = useSelector((state) => state.common);
  const userType = getUserType();
  const extranalAccounts = externalUserDetails();
  const extranalUserName = extranalAccounts ? extranalAccounts?.userProfile?.myNCRUserProfileResp?.firstName+' '+extranalAccounts?.userProfile?.myNCRUserProfileResp?.lastname : null;
  const isExtranalUser = (userType === USER_TYPE.externalUser);
  const customization = useSelector((state) => state.layout);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef(null);
  const handleLogout = async () => {
    if(isExtranalUser){
      externalUserLogout();
    }else{
      logout();
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleListItemClick = (event, index, route = '') => {
    setSelectedIndex(index);
    handleClose(event);

    if (route && route !== '') {
      navigate(route);
    }
  };
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);



  // Code Start for Refresh Token, Show Popup after 1.45 mints, Logout after 15 mins after display alert.
  const [initialCount, setInitialCount] = useState(-1);
  const [remainingTime, setRemainingTime] = useState(-1);
  const idleTime45Mintus = 105 * 60; // 1.45m minutes, 105M * 60S
  const countDown15Minuts = 900; // 15 Mintus count Down in Sec. 900S = 15M
  const [showSessionAlert, setShowSessionAlert] = useState(false);
  const [alertRemainingTime, setAlertRemainingTime] = useState(false);
  const [alertInitialCount, setAlertInitialCount] = useState(-1);
  const [isActive, setIsActive] = useState(1);
  const handleUserActivity = () => {
    setShowSessionAlert(false);
    setIsActive((prevValue) => prevValue + 1);
  };

  useEffect(() => {
    setAlertInitialCount(idleTime45Mintus);
    window.addEventListener('click', handleUserActivity);
    return () => {
        window.removeEventListener('click', handleUserActivity);
    };
  }, []);

  // THIS IS FOR SHOW ALERT POPUP AFTER 1.45 MINUTES.
  useEffect(() => {
    if(alertInitialCount >= 0 && isActive){
    const alertTargetTime = Date.now() + alertInitialCount * 1000; // SET 1.45 MINUTES forward.
    const alertRemainingTimer = () => {
      const currentTime = Date.now();
      const alertTimeLeft = Math.max(0, Math.round((alertTargetTime - currentTime) / 1000));
      setAlertRemainingTime(alertTimeLeft);
      if (alertTimeLeft <= 0) {
        clearInterval(newInterval);
      }
    };
    let newInterval = setInterval(alertRemainingTimer, 1000);
    alertRemainingTimer();
    return () => clearInterval(newInterval);
  }
  }, [alertInitialCount, isActive]);

  // IF TIME IS 0 THEN ALERT WILL BE DISPLAYED HERE.
  useEffect(()=>{
    if(alertRemainingTime !== false && alertRemainingTime <= 0){
      setShowSessionAlert(true);
    }
  },[alertRemainingTime]);
  

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${hours ? hours.toString().padStart(1, '0')+':' : '' }${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`;
  };

  useEffect(()=>{
    if(showSessionAlert){
      setInitialCount(countDown15Minuts); // 15 Mintus count Down in Sec. 900S = 15M
    }
  },[showSessionAlert]);

  // THIS IS FOR START COUNDOWN AND GET LOGUT AFTER 5 MINUTES.
  useEffect(() => {
    if(initialCount >= 0 && showSessionAlert){
    const targetTime = Date.now() + initialCount * 1000; // SET 5 MINUTES
    const updateRemainingTime = () => {
      const currentTime = Date.now();
      const timeLeft = Math.max(0, Math.round((targetTime - currentTime) / 1000));
      setRemainingTime(timeLeft);
      if (timeLeft <= 0) {
        clearInterval(interval);
      }
    };
    let interval = setInterval(updateRemainingTime, 1000);
    // Initial update in case the interval is delayed
    updateRemainingTime();
    return () => clearInterval(interval);
  }
  }, [initialCount, showSessionAlert]);

  useEffect(() => {
    if(showSessionAlert){
      document.title = formatTime(remainingTime !== -1 ?
        remainingTime : 0);
    }else {
      document.title = applicationTitle.title; 
    }
    if (remainingTime === 0 && showSessionAlert) {
      handleLogout();
      setShowSessionAlert(false);
    }
  }, [remainingTime, showSessionAlert]);


  return (
    <>
      <Chip
        sx={{
          height: '48px',
          alignItems: 'center',
          borderRadius: '0px',
          transition: 'all .2s ease-in-out',
          borderColor: theme.palette.primary.light,
          backgroundColor: 'transparent',
          border: 'none',
          '&[aria-controls="menu-list-grow"], &:hover': {
          },
          '& .MuiChip-label': {
            lineHeight: 0,
          },
        }}
        icon={
          <Avatar
            sx={{
              ...theme.typography.mediumAvatar,
              margin: '8px 0 8px 8px !important',
              cursor: 'pointer',
              color: '#fff'
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            color="inherit"
          />
        }
        label={<span className="username-dropdown">{isExtranalUser && extranalUserName ? extranalUserName : accounts?.length > 0 && accounts[0]?.name}</span>}
        variant="outlined"
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        color="primary"
      />
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  border={false}
                  elevation={16}
                  content={false}
                  boxShadow
                  shadow={theme.shadows[16]}
                >
                  <Box sx={{ p: 2 }}>
                    <Stack sx={{ paddingBottom: '20px' }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4">Welcome</Typography>
                        <Typography
                          component="span"
                          variant="h4"
                          sx={{ fontWeight: 400 }}
                        >
                          {isExtranalUser && extranalUserName ? extranalUserName : accounts?.length > 0 && accounts[0]?.name }

                        </Typography>
                      </Stack>
                      <Stack sx={{ mt: 2 }}>
                        {userPermissions?.userAuthorizations[0]?.roleName && (
                          <Typography component="span">
                            <strong>User Role :</strong>{' '}
                            {userPermissions?.userAuthorizations[0]?.roleName}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                    <Divider />
                    <div>
                      <List
                        component="nav"
                        sx={{
                          width: '100%',
                          maxWidth: 350,
                          minWidth: 300,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: '10px',
                          [theme.breakpoints.down('md')]: {
                            minWidth: '100%',
                          },
                          '& .MuiListItemButton-root': {
                            mt: 0.5,
                          },
                        }}
                      >
                        
                          <>
                           
                            <ListItemButton
                              sx={{
                                borderRadius: `${customization.borderRadius}px`,
                              }}
                              selected={selectedIndex === 4}
                              onClick={handleLogout}
                            >
                              <ListItemIcon>
                                <IconLogout stroke={1.5} size="1.3rem" />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2">Logout</Typography>
                                }
                              />
                              <div id='logoutTimer'>{formatTime(alertRemainingTime) || '1.45:00'}</div>
                            </ListItemButton>
                          </>
                      </List>
                    </div>
                  </Box>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
      <AlertBox
      open={showSessionAlert}
      onClose={() => setShowSessionAlert(false)}
      title={`Your session is about to expire in ${formatTime(remainingTime !== -1 ?
        remainingTime : 0
      )} minutes. Click on the screen to continue your session.`}
      avatars={<img src={WarningIcon} alt="warning" />}
    />
    </>
  );
};

export default ProfileSection;
